<template>
  <div class="client-record">
    <div v-if="confirmed" class="client-details">
      <div class="left">
        <div class="svg-box" :style="{ backgroundColor: client.avatarColor }">
          <inline-svg :src="$assets.svg.avatar[client.avatarKey]" width="75%" height="75%" fill="#FFF" />
        </div>
        <p class="id">
          Client ID:
          <span class="value">{{ client.code }}</span>
        </p>
        <div class="edit-buttons">
          <div v-if="editing">
            <button type="button" class="client-button highlight" @click="showSaveConfirmation = true">Save</button>
            <button type="button" class="client-button" @click="cancelEdits">Cancel</button>
          </div>
          <button v-else type="button" class="client-button" @click="editing = true">Edit Information</button>
        </div>
        <button type="button" class="client-button" @click="showResetConfirmation = true">Reset Client Password</button>
      </div>

      <div class="right">
        <ul class="client-selectors">
          <div v-if="editing">
            <li>
              <p class="label">Gender Identity</p>
              <Selector
                aria-label="gender list"
                :optionList="genderList"
                :selectedKey="selectedGender.id"
                :onChange="selectGender"
              />
            </li>
            <li>
              <p class="label">Ethnic Identity</p>
              <Selector
                aria-label="ethno list"
                :optionList="ethnoList"
                :selectedKey="selectedEthno.id"
                :onChange="selectEthno"
              />
            </li>
            <li>
              <p class="label">Registered Client Age</p>
              <Selector
                aria-label="age list"
                :optionList="ageList"
                :selectedKey="selectedAge.id"
                :onChange="selectAge"
              />
            </li>
            <li>
              <p class="label">Referral Path</p>
              <Selector
                aria-label="referral list"
                :optionList="referralList"
                :selectedKey="selectedReferral.id"
                :onChange="selectReferral"
              />
            </li>
          </div>
          <div v-else>
            <li>
              <p class="label">Gender Identity</p>
              <p class="client-field">{{ client.gender_meta_value }}</p>
            </li>
            <li>
              <p class="label">Ethnic Identity</p>
              <p class="client-field">{{ client.ethno_meta_value }}</p>
            </li>
            <li>
              <p class="label">Registered Client Age</p>
              <p class="client-field">{{ client.age_meta_value }}</p>
            </li>
            <li>
              <p class="label">Referral Path</p>
              <p class="client-field">{{ client.path_meta_value }}</p>
            </li>
          </div>
          <li>
            <p class="label">Registered Date</p>
            <p class="client-field">{{ client.registerTime }}</p>
          </li>
          <li>
            <p class="label">Registered Facilitator</p>
            <p class="client-field">{{ client.fFirstName + ' ' + client.fLastName }}</p>
          </li>
        </ul>
      </div>
    </div>

    <ClientModal v-else class="password-modal" width="513px" height="243px" @close="$router.go(-1)">
      <h1 class="modal-header">Security</h1>
      <p class="modal-description small-margin">To enter the Client Record page, please enter your password below.</p>
      <PasswordConfirmationField v-model="checkPassword" @confirmed="confirmed = true" />
      <button type="button" class="confirm-button" @click="checkPassword = true">ACCESS NOW</button>
    </ClientModal>

    <ClientModal
      v-if="showSaveConfirmation"
      class="confirmation-modal"
      width="521px"
      height="243px"
      @close="showSaveConfirmation = false"
    >
      <h1 class="modal-header">Client Information Change</h1>
      <p class="modal-description">
        You are about to change your client’s information. To continue, please confirm with the “Save / Confirm” button
        below.
      </p>
      <button type="button" class="confirm-button" @click="saveClient">SAVE / CONFRIM</button>
    </ClientModal>

    <ClientModal
      v-if="showResetConfirmation"
      class="confirmation-modal"
      width="521px"
      height="220px"
      @close="showResetConfirmation = false"
    >
      <h1 class="modal-header">Reset Client’s Password</h1>
      <p class="modal-description">
        You are about to reset your clients’ password. To continue, please confirm with the “Reset Password” button
        below.
      </p>
      <button type="button" class="confirm-button" @click="resetPassword">RESET PASSWORD</button>
    </ClientModal>

    <ClientPasswordPopup
      v-if="showNewPassword"
      :title="newPasswordPopup.title"
      :description="newPasswordPopup.description"
      :passwordClientList="passwordClientList"
      @close="closePasswordList"
    />

    <GlobalLoading v-if="isLoading" />
  </div>
</template>

<script setup>
import { ref, onBeforeMount, computed } from 'vue';

import $assets from '@/assets';
import ClientModal from '@/components/client/ClientModal.vue';
import ClientPasswordPopup from '@/components/client/ClientPasswordPopup.vue';
import PasswordConfirmationField from '@/components/client/PasswordConfirmationField.vue';
import Selector from '@/components/common/DropdownSelector.vue';
import GlobalLoading from '@/components/common/GlobalLoading.vue';
import ClientService from '@/services/client';
import MetaService from '@/services/meta';
import useSessionStore from '@/stores/session';

const SessionStore = useSessionStore();
const editing = ref(false);
const confirmed = ref(false);

const checkPassword = ref(false);

const genderList = ref([]);
const ethnoList = ref([]);
const ageList = ref([]);
const referralList = ref([]);

const selectedGender = ref(null);
const selectedEthno = ref(null);
const selectedAge = ref(null);
const selectedReferral = ref(null);

const showSaveConfirmation = ref(false);

const newPasswordPopup = ref({
  title: 'Reset Client’s Password',
  description:
    "Your client's Password has been reset. Please take a moment to copy the password to a safe location. The password will be encrypted once the client profile is registered. For security purpose, our system will not be able to decrypt the password for you.",
});

const showResetConfirmation = ref(false);
const isLoading = ref(false);
const showNewPassword = ref(false);
const passwordClientList = ref([]);

const client = computed(() => {
  const { currentClient } = SessionStore.HUBSettings;

  return currentClient
    ? {
        ...currentClient,
        avatarColor: currentClient.avatar_color_meta_value,
        avatarKey: currentClient.avatar_meta_value,
        registerTime: currentClient.register_time,
        fFirstName: currentClient.facilitator_first_name,
        fLastName: currentClient.facilitator_last_name,
      }
    : {};
});

const ageGroup = computed(() => {
  if (selectedAge.value && selectedAge.value.id > 0) {
    return selectedAge.value.name === '24+' ? 'adult' : 'youth';
  }

  return null;
});

async function loadClientData() {
  selectedGender.value = { id: client.value.gender_meta_id, name: client.value.gender_meta_value };
  selectedEthno.value = { id: client.value.ethno_meta_id, name: client.value.ethno_meta_value };
  selectedAge.value = { id: client.value.age_meta_id, name: client.value.age_meta_value };
  selectedReferral.value = { id: client.value.path_meta_id, name: client.value.path_meta_value };
}

onBeforeMount(async () => {
  loadClientData();

  const genderData = await MetaService.getMetasByType('gender');
  genderList.value = genderData.records.map((meta) => ({ id: meta.id, name: meta.meta_value }));

  const ethnoData = await await MetaService.getMetasByType('ethno');
  ethnoList.value = ethnoData.records.map((meta) => ({ id: meta.id, name: meta.meta_value }));

  const ageData = await MetaService.getMetasByType('age');
  ageList.value = ageData.records.map((meta) => ({ id: meta.id, name: meta.meta_value }));

  const referralData = await MetaService.getMetasByType('referral_path');
  referralList.value = referralData.records
    .filter((referral) => (ageGroup.value === 'youth' ? referral.meta_mark !== 'adult' : true))
    .map((meta) => ({ id: meta.id, name: meta.meta_value }));
});

function selectGender(data) {
  selectedGender.value = data;
}
function selectEthno(data) {
  selectedEthno.value = data;
}
function selectAge(data) {
  selectedAge.value = data;
}
function selectReferral(data) {
  selectedReferral.value = data;
}
async function saveClient() {
  showSaveConfirmation.value = false;

  const clientData = {
    id: client.value.id,
  };

  if (selectedGender.value.id) {
    clientData.gender_meta_id = selectedGender.value.id;
    clientData.gender_meta_value = selectedGender.value.name;
  }

  if (selectedAge.value.id) {
    clientData.age_meta_id = selectedAge.value.id;
    clientData.age_meta_value = selectedAge.value.name;
  }

  if (selectedEthno.value.id) {
    clientData.ethno_meta_id = selectedEthno.value.id;
    clientData.ethno_meta_value = selectedEthno.value.name;
  }

  if (selectedReferral.value.id) {
    clientData.path_meta_id = selectedReferral.value.id;
    clientData.path_meta_value = selectedReferral.value.name;
  }

  const cleintResult = await ClientService.updateClient(client.value.id, clientData);
  SessionStore.setHUBSettingItems({
    currentClient: cleintResult.record,
  });
  loadClientData();
  editing.value = false;
}
function cancelEdits() {
  loadClientData();
  editing.value = false;
}
async function resetPassword() {
  showResetConfirmation.value = false;
  isLoading.value = true;

  const passResetResult = await ClientService.resetPassword(client.value.id);
  passwordClientList.value = [passResetResult.record];

  isLoading.value = false;
  showNewPassword.value = true;
}
function closePasswordList() {
  passwordClientList.value = [];
  showNewPassword.value = false;
}
</script>

<style scoped>
.client-details {
  position: absolute;
  top: 15%;
  left: 40%;
  transform: translateX(-50%);

  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.left {
  width: 10em;
  margin-right: 4em;

  font-size: 20px;
}
.svg-box {
  margin: 0 0.5em;
  width: 9em;
  height: 9em;
  border-radius: 50%;

  display: flex;
  justify-content: center;
  align-items: center;
}
.left .id {
  width: 100%;
  text-align: center;
  font-size: 20px;
}
.left .id .value {
  font-weight: bold;
}
.edit-buttons {
  width: 100%;
  margin: 17px 0;
}
.client-button {
  width: 100%;
  padding: 0.8em;

  color: #8e8e8e;
  background-color: white;
  border: none;
  border-radius: 9px;
  filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.25));

  cursor: pointer;
}
.client-button.highlight {
  color: white;
  background: #62a517;
}
.edit-buttons .client-button {
  margin: 2.5px 0;
}

.right {
  width: 21em;
  font-size: 16px;
}
.client-selectors > li {
  margin: 1em 0;
}
.client-field {
  height: calc(2.5em + 2px); /* made to match the height of the Selector components */
  margin: 0;
  padding: 0 1em;

  display: flex;
  align-items: center;
  border: 1px solid #a7a9ac;
  border-radius: 5px;
  box-sizing: border-box;
}
.label {
  margin: 0.5em 0;
  line-height: 20px;
}

.modal-header {
  margin-top: 0;
}
.modal-description {
  width: 100%;
}
.modal .confirm-button {
  height: 42px;
  width: 210px;

  background: #62a517;
  border: none;
  border-radius: 9px;

  color: #ffffff;
  text-align: center;

  font-family: 'LatoRegular';
  font-weight: 700;
  font-size: 15px;

  cursor: pointer;
}

.confirmation-modal >>> .modal {
  justify-content: space-around;
}
.confirmation-modal .modal-header {
  margin: 0;
}
.confirmation-modal .modal-description {
  margin-bottom: 3em;
}
</style>
